<template>
    <v-card class="pa-3 mb-3" outlined min-height="152">
        <v-row no-gutters>
            <v-col cols="0">
                <h5 v-text="'Playground'" />
            </v-col>
            <v-col cols="auto">
                <label>
                    Start
                    <input type="time" v-model="startTimeStr" class="timeInput">
                </label>
            </v-col>
            <v-col cols="auto" class="pl-3">
                <label>
                    End
                    <input type="time" v-model="endTimeStr" class="timeInput">
                </label>
            </v-col>
            <!-- 
            <v-col cols="auto" class="pl-3">
                <label>
                    Jobs
                    <input type="number" min="1" v-model.number="jobsCount" class="timeInput" style="width: 70px;">
                </label>
            </v-col>
            -->
        </v-row>

        <div v-if="initiated && isValid" class="timeline-layout">

            <!-- LEFT SIDE: all bars -->
            <div class="timeline-left">

                <!-- actual -->
                <div class="actual-bar-wrapper">
                    Real time: {{ elapsedMinutes }} min
                    <div class="actual-bar">
                        <div class="actual-bar-segment" :style="{ width: actualPos + '%' }">
                            <div class="actual-bar-start"></div>
                            <div class="actual-bar-end"></div>
                        </div>
                    </div>
                </div>

                <!-- main timeline -->
                <div class="timeline">
                    <div class="segment min" :style="{ width: minWidth + '%' }">
                        Minimum ({{ minimumMinutes }}m)
                    </div>

                    <div class="segment increments" :style="{ width: incrementsWidth + '%' }">
                        <div v-for="(n, index) in totalSegments" :key="index" class="increment"
                            :class="{ 'increment--unbilled': isIncrementUnbilled(index) }"
                            :style="{ width: (100 / totalSegments) + '%' }">
                            <!-- +{{ thresholdMinutes }}m -->
                        </div>
                    </div>

                    <div class="marker actual" :style="{ left: actualPos + '%' }"></div>
                </div>

                <!-- billed -->
                <div class="billed-bar-wrapper">
                    <div class="billed-bar">
                        <div class="billed-bar-segment" :style="{ width: billedPos + '%' }">
                            <div class="billed-bar-start"></div>
                            <div class="billed-bar-end"></div>
                        </div>
                    </div>

                    <div class="mt-1"><b>Rounded time: {{ billedMinutes }} min</b></div>
                    <div class="mt-1" v-if="loading">Calculating…</div>
                    <div class="mt-1" v-else-if="serverFormattedText">Server: {{ serverFormattedText }}</div>
                    <div class="mt-1" v-if="serverStartText">Rounded start: {{ serverStartText }}</div>
                </div>

            </div>

            <!-- RIGHT SIDE: vertical legend -->
            <div class="timeline-legend">
                <div class="legend-item">
                    <span class="color-box color-min"></span>
                    Minimum duration per entry
                </div>

                <div class="legend-item">
                    <span class="color-box color-inc"></span>
                    Rounding step after minimum ({{ thresholdMinutes }} min)
                </div>
                <!--
                <div class="legend-item">
                    <span class="color-box color-inc"></span>
                    Start rounds down to every {{ incrementMinutes }} minutes
                </div>
                -->
            </div>

        </div>

        <p v-else>
            Please enter valid start/end times.
        </p>
    </v-card>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";
export default {
    name: "TimeRoundingVisualizer",
    mixins: [clientAPI],
    props: {
        initiated: Boolean,
        minimumMinutes: Number,
        incrementMinutes: Number,
        thresholdMinutes: Number,
        isForWork: {
            type: Boolean,
            default: true
			},
			isSaving: {
				type: Boolean,
				default: false
        }
    },

    data() {
        return {
            startTimeStr: "09:00",
            endTimeStr: "10:25",
            jobsCount: 1,
            backend: null,
            loading: false,
				pendingSettingChange: false,
        };
    },

    methods: {
        toIsoForToday(timeStr) {
            if (!timeStr) return null;
            const [hStr, mStr] = timeStr.split(":");
            const h = parseInt(hStr, 10);
            const m = parseInt(mStr, 10);
            if (isNaN(h) || isNaN(m)) return null;
            const d = new Date();
            d.setHours(h, m, 0, 0);
            return d.toISOString();
        },

        async fetchBackend() {
				// Wait until settings are saved server-side
				if (this.isSaving) return;

            if (!this.isValid) {
                this.backend = null;
                return;
            }
            this.loading = true;
            try {
                const dto = {
                    originalStartTime: this.toIsoForToday(this.startTimeStr),
                    originalEndTime: this.toIsoForToday(this.endTimeStr),
                    numberOfChildJobs: this.jobsCount > 0 ? this.jobsCount : 1,
                    isForWork: this.isForWork
                };
                // this method is provided by ClientAPIMixin at component usage site
                const response = await this.$parent.testTimeReporting
                    ? this.$parent.testTimeReporting(dto)
                    : (this.testTimeReporting ? this.testTimeReporting(dto) : null);
                this.backend = response;
            } catch (e) {
                this.backend = null;
            } finally {
                this.loading = false;
            }
        },

        toMinutes(timeStr) {
            if (!timeStr) return null;
            const parts = timeStr.split(":");
            if (parts.length < 2) return null;
            const h = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            if (isNaN(h) || isNaN(m)) return null;
            return h * 60 + m;
        },

        // used in v-for for increment opacity
        isIncrementUnbilled(index) {
            if (!this.isValid) return false;
            return index >= this.billedIncrementCount;
        }
    },

    computed: {
        isValid() {
            return (
                this.startMinutes != null &&
                this.endMinutes != null &&
                this.endMinutes > this.startMinutes
            );
        },

        serverBilledMinutes() {
            return this.backend?.timeSpentTotalMinutes ?? null;
        },

        serverFormattedText() {
            return this.backend?.formattedText ?? null;
        },
        serverStartText() {
            if (!this.backend?.startTime) return null;
            try {
                const d = new Date(this.backend.startTime);
                const hh = d.getHours().toString().padStart(2, '0');
                const mm = d.getMinutes().toString().padStart(2, '0');
                return `${hh}:${mm}`;
            } catch {
                return null;
            }
        },

        startMinutes() {
            return this.toMinutes(this.startTimeStr);
        },
        endMinutes() {
            return this.toMinutes(this.endTimeStr);
        },
        elapsedMinutes() {
            if (!this.isValid) return 0;
            return this.endMinutes - this.startMinutes;
        },
        billedMinutes() {
            if (!this.isValid) return 0;
            // Prefer server result for accuracy
            if (this.serverBilledMinutes != null) return this.serverBilledMinutes;

            // Fallback local approximation if server not available
            const min = this.minimumMinutes || 0;
            const step = this.thresholdMinutes || 0;

            if (this.elapsedMinutes <= min) return min;

            if (step <= 1) {
                // No step rounding after minimum
                return this.elapsedMinutes;
            }

            const roundedUpSteps = Math.ceil(this.elapsedMinutes / step);
            return Math.max(min, roundedUpSteps * step);
        },
        totalSegments() {
            if (!this.isValid) return 1;
            const step = this.thresholdMinutes || 1;
            const displayTotal = Math.max(this.minimumMinutes, this.elapsedMinutes, this.billedMinutes);
            return Math.max(1, Math.ceil(displayTotal / step));
        },
        totalMinutes() {
            if (!this.isValid) return 1;
            const step = this.thresholdMinutes || 1;
            const displayTotal = Math.max(this.minimumMinutes, this.elapsedMinutes, this.billedMinutes);
            return Math.ceil(displayTotal / step) * step;
        },

        /* positions (percent of total) */

        minWidth() {
            return (this.minimumMinutes / this.totalMinutes) * 100;
        },
        incrementsWidth() {
            return 100 - this.minWidth;
        },
        actualPos() {
            return (this.elapsedMinutes / this.totalMinutes) * 100;
        },
        billedPos() {
            return (this.billedMinutes / this.totalMinutes) * 100;
        },

        /* how many full increments are billed */
        billedIncrementCount() {
            if (!this.isValid) return 0;
            const step = this.thresholdMinutes || 1;
            const billedSpan = Math.max(0, this.billedMinutes);
            return Math.max(0, Math.floor(billedSpan / step));
        }
    },

    watch: {
        initiated(newVal) {
            if (newVal) this.fetchBackend();
        },
        startTimeStr() {
            this.fetchBackend();
        },
        endTimeStr() {
            this.fetchBackend();
        },
        jobsCount() {
            this.fetchBackend();
        },
        isForWork() {
            this.fetchBackend();
			},
			minimumMinutes() {
				// Defer refresh until save completes
				this.pendingSettingChange = true;
			},
			incrementMinutes() {
				this.pendingSettingChange = true;
			},
			thresholdMinutes() {
				this.pendingSettingChange = true;
			},
			isSaving(newVal, oldVal) {
				// When saving finishes, refresh from server to reflect persisted settings
				if (oldVal === true && newVal === false) {
					if (this.pendingSettingChange) {
						this.pendingSettingChange = false;
						this.fetchBackend();
					} else {
						// Even if nothing flagged, ensure we reflect any external save
						this.fetchBackend();
					}
				}
			}
    },

    mounted() {
        if (this.initiated) {
            this.fetchBackend();
        }
    }
};
</script>


<style lang="scss" scoped>
.timeInput {
    border-radius: 4px;
    border: 1px solid grey;
}

.timeline-wrapper {
    margin-top: 12px;
    max-width: 500px;
}

/* actual time bar above */
.actual-bar-wrapper {
    max-width: 500px;
    margin-bottom: 6px;
}

.actual-bar {
    position: relative;
    height: 12px;
    width: 100%;
}

/* horizontal line centered vertically */
.actual-bar-segment {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 0;
    border-top: 1px solid #000;
}

/* ticks starting on the line and going DOWN */
.actual-bar-start,
.actual-bar-end {
    position: absolute;
    width: 0;
    height: 8px;
    border-left: 1px solid #000;
    top: 50%;
}

.actual-bar-start {
    left: 0;
}

.actual-bar-end {
    right: 0;
}

/* billed bar below (same color as others) */

.billed-bar-wrapper {
    max-width: 500px;
    margin-top: 6px;
}

.billed-bar {
    position: relative;
    height: 8px;
    width: 100%;
}

.billed-bar-segment {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 0;
    border-top: 2px solid #000;
}

/* ticks up, aligned to start/end */
.billed-bar-start,
.billed-bar-end {
    position: absolute;
    width: 0;
    height: 8px;
    border-left: 2px solid #000;
    bottom: 0;
}

.billed-bar-start {
    left: 0;
}

.billed-bar-end {
    right: 0;
}

/* main timeline */

.timeline {
    position: relative;
    height: 30px;
    border: 1px solid #ffffff;
    display: flex;
    width: 100%;
}

.segment {
    height: 100%;
}

.segment.min {
    background: var(--v-primary-base);
    display: flex;
    align-items: center;
    color: white;
    padding-left: 8px;
}

/* increments container – transparent so individual segments control color/opacity */
.segment.increments {
    display: flex;
    background: transparent;
}

/* each increment segment */
.increment {
    border-left: 2px solid #ffffff;
    height: 100%;
    box-sizing: border-box;
    background: var(--v-disabled-darken3);
}

/* unbilled increments – half opacity */
.increment--unbilled {
    opacity: 0.3;
}

/* markers */
.marker {
    position: absolute;
    top: -6px;
    bottom: -6px;
    width: 2px;
}

/* trigger marker – dashed line, always inside an increment */
.marker.trigger {
    background: transparent;
    border-left: 2px dashed var(--v-secondary-base);
}

/* legend */

.legend {
    margin-top: 4px;
    font-size: 13px;
}

.legend span {
    margin-right: 16px;
    display: inline-flex;
    align-items: center;
}

.color-box {
    width: 8px;
    height: 8px;
    margin-right: 4px;
}

.color-min {
    background: var(--v-primary-base);
}

.color-inc {
    background: var(--v-disabled-darken3);
}

/* === LAYOUT === */

.timeline-layout {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.timeline-left {
    flex: 1;
    /* bars take full width */
    min-width: 0;
}

.timeline-legend {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 200px;
    /* adjust if needed */
    padding-top: 12px;
    font-size: 13px;
}

/* legend rows */
.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

/* dashed trigger legend line */
.trigger-legend-line {
    width: 0;
    height: 14px;
    border-left: 2px dashed var(--v-secondary-base);
}

/* trigger legend – dashed vertical line */
.legend-trigger {
    display: inline-flex;
    align-items: center;
}

.trigger-legend-line {
    width: 0;
    height: 13px;
    border-left: 2px dashed var(--v-secondary-base);
    margin-right: 4px;
}
</style>